import { useEffect, useMemo } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Google-provided demo map id — enables AdvancedMarker without a cloud-configured style.
const MAP_ID = "DEMO_MAP_ID";

const toLatLng = (pt) => ({ lat: pt[0], lng: pt[1] });

const PIN_CLASS = {
  driver: "driver-pin animate-pulse-pin",
  pickup: "pickup-pin",
  dropoff: "dropoff-pin",
};

// Fits the viewport to the given points whenever `fitKey` changes.
function FitController({ fitPoints, fitKey }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !fitPoints || fitPoints.length === 0) return;
    if (fitPoints.length === 1) {
      map.setCenter(toLatLng(fitPoints[0]));
      map.setZoom(13);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    fitPoints.forEach((p) => bounds.extend(toLatLng(p)));
    map.fitBounds(bounds, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey]);
  return null;
}

// Renders dashed polylines (route legs) imperatively — the lib has no Polyline component.
function Routes({ routes }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    const lines = routes.map((r) =>
      new window.google.maps.Polyline({
        map,
        path: r.points.map(toLatLng),
        strokeColor: r.color || "#0E3B6E",
        strokeOpacity: 0,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 },
            offset: "0",
            repeat: "12px",
          },
        ],
      }),
    );
    return () => lines.forEach((l) => l.setMap(null));
  }, [map, routes]);
  return null;
}

export default function MapView({
  center = [49.2827, -123.1207],
  zoom = 12,
  markers = [],
  routes = [],
  fit = null,
  fitKey = "",
  height = "100%",
}) {
  // Auto-fit to markers when the caller doesn't pass an explicit fit set.
  const fitPoints = useMemo(() => {
    if (fit && fit.length) return fit;
    return markers.filter((m) => m.lat != null && m.lng != null).map((m) => [m.lat, m.lng]);
  }, [fit, markers]);

  const effectiveFitKey =
    fitKey || fitPoints.map((p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`).join("|");

  if (!API_KEY) {
    return (
      <div
        style={{ height, width: "100%" }}
        className="flex items-center justify-center bg-muted text-muted-foreground font-mono text-xs uppercase tracking-widest"
        data-testid="map-container"
      >
        Google Maps API key not configured
      </div>
    );
  }

  return (
    <div style={{ height, width: "100%" }} data-testid="map-container">
      <APIProvider apiKey={API_KEY}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={toLatLng(center)}
          defaultZoom={zoom}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
          clickableIcons={false}
        >
          <FitController fitPoints={fitPoints} fitKey={effectiveFitKey} />
          <Routes routes={routes} />
          {markers.map((m, i) => (
            <AdvancedMarker
              key={i}
              position={{ lat: m.lat, lng: m.lng }}
              title={m.title || m.type}
            >
              <div className={PIN_CLASS[m.type] || PIN_CLASS.driver} />
            </AdvancedMarker>
          ))}
        </Map>
      </APIProvider>
    </div>
  );
}
